import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { formatPhoneNumber } from "react-phone-number-input";

import { Heading, Paragraph } from "common/core/typography";
import Button from "common/core/button";
import { SensitiveEmail, SensitivePhoneNumber } from "common/details/identity";
import { userFullName } from "util/user";
import { getParentPathByOffset } from "util/location";
import { findMaximumRiskInfo } from "common/details/identity/util";
import { usePermissions } from "common/core/current_user_role";
import RiskLevel from "common/dashboard/risk_level";
import DownloadProofIdentityReport from "common/details/identity/common/download_proof_identity_report";
import {
  IdentityTabV2AnalyticsEvent,
  useIdentityTabV2Analytics,
} from "common/details/identity/v2/analytics";
import { Spacer } from "common/details/identity/v2/components/spacer";
import { CURRENT_PORTAL } from "constants/app_subdomains";
import Apps from "constants/applications";

import type { HeaderCustomerSigner as CustomerSigner } from "./index.fragment.graphql";
import Styles from "./index.module.scss";

type Props = {
  customerSigner: CustomerSigner;
  documentBundle: {
    organizationTransaction: {
      id: string;
    };
  };
  proofDefend: boolean;
};

function ContactInfo({ customerSigner }: { customerSigner: CustomerSigner }) {
  const { email, phone } = customerSigner;
  const formattedPhoneNumber = phone && formatPhoneNumber(`+${phone.countryCode}${phone.number}`);

  return (
    <Paragraph className={Styles.subheader}>
      <span>{customerSigner.userId}</span>

      {formattedPhoneNumber && (
        <span>
          <Spacer />
          <SensitivePhoneNumber phoneNumber={formattedPhoneNumber} />
        </span>
      )}

      {email && (
        <span>
          <Spacer />
          <SensitiveEmail email={email} />
        </span>
      )}
    </Paragraph>
  );
}

export function Header({ customerSigner, documentBundle, proofDefend }: Props) {
  const { hasPermissionFor } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const track = useIdentityTabV2Analytics();
  const maximumRiskInfo = useMemo(() => findMaximumRiskInfo([customerSigner]), [customerSigner]);
  const IS_KEYSTONE = CURRENT_PORTAL === Apps.ADMIN;

  const identityProfilePath = IS_KEYSTONE
    ? `${getParentPathByOffset(pathname, 3)}/identities/${customerSigner.userId}`
    : `/identities/${customerSigner.userId}`;

  const handleViewProfile = useCallback(() => {
    track?.(IdentityTabV2AnalyticsEvent.VIEW_PROFILE_CLICKED);
    navigate(identityProfilePath);
  }, [track, navigate, identityProfilePath]);

  return (
    <div className={Styles.container}>
      <div>
        <div>
          <Heading level="h2" textStyle="headingThree" sensitive="mask">
            {userFullName(customerSigner)}
          </Heading>
          {proofDefend && (
            <RiskLevel level={maximumRiskInfo?.riskLevel ?? null} textLength="long" />
          )}
        </div>

        <div>
          <Button variant="secondary" buttonColor="action" onClick={handleViewProfile}>
            <FormattedMessage
              id="38dcb599-d871-4f6a-81b7-74816986f4cd"
              defaultMessage="View profile"
            />
          </Button>
          {proofDefend && hasPermissionFor("generateProofIdentityReport") && (
            <DownloadProofIdentityReport
              organizationTransactionId={documentBundle.organizationTransaction.id}
              withIcon={false}
            />
          )}
        </div>
      </div>

      <ContactInfo customerSigner={customerSigner} />
    </div>
  );
}
